import { closeAppIssueReportDrawer } from '@/redux/actions/uiActions/drawerActions';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import selectors from '@/redux/selectors/selectors';
import { getAppDefects, reportAppDefect } from '@/redux/actions/gravityActions';
import sendToast from '@/lib/sendToast';
import memoizeSelector from '@/redux/lib/memoizeSelector.js';
import appScreenSelectors from '@/screens/apps/redux/selectors/appScreenSelectors';
import { appFixCategories, APP_FIX_CATEGORY_KEYS } from '@shared/apps/appFixCategories.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Button from './Button';
import Input from './Input';
import Panel from './panel/Panel';
import ScrollContainerV2 from './v2/ScrollContainerV2';
import SliderHeader from './arguments/argumentOptionsSlider/SliderHeader';
import DividerV2 from './v2/DividerV2';
import AppEditorDefectListRow, { ReportedDefectsSkeleton } from './AppEditorDefectListRow';

const MAX_REPORT_LENGTH = 5000;
const NO_ACTION_VALUE = '__none__';

const categoryOptions = APP_FIX_CATEGORY_KEYS.map((key) => ({
  value: key,
  label: appFixCategories[key].label,
  subLabel: appFixCategories[key].description
}));

const AppEditorIssueReportDrawer = () => {
  const appId = useArgSelector(selectors.param, 'appId');
  const drawerState = useSelector(memoizeSelector('appIssueReportDrawer', (state) => state.ui.appIssueReportDrawer)) || {};
  const appActions = useSelector(memoizeSelector('appActions', (state) => appScreenSelectors.activeApp(state)?.actions));
  const appFixerUpdate = useSelector((state) => state.ui.appFixerUpdate);
  const open = drawerState?.open;

  const [category, setCategory] = useState(null);
  const [actionId, setActionId] = useState(NO_ACTION_VALUE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // null means "not loaded yet" so the list shows a skeleton on first load,
  // instead of flashing the empty state before the request lands.
  const [defects, setDefects] = useState(null);

  // The report text is uncontrolled: the DOM owns it, so typing triggers no
  // React re-render on this heavy editor page. We read it from the ref on
  // submit and track only an empty <-> non-empty boolean for the Submit button,
  // so a whole report costs at most one re-render.
  const textareaRef = useRef(null);
  const hasTextRef = useRef(false);
  const [hasText, setHasText] = useState(false);

  const handleReportInput = (event) => {
    const next = event.target.value.trim().length > 0;
    if (next !== hasTextRef.current) {
      hasTextRef.current = next;
      setHasText(next);
    }
  };

  const actionOptions = useMemo(() => [{ value: NO_ACTION_VALUE, label: 'No specific action' }, ...(appActions?.map((action) => ({ value: action.id, label: action.name })) || [])], [appActions]);

  const selectedCategoryOption = categoryOptions.find((option) => option.value === category);
  const selectedActionOption = actionOptions.find((option) => option.value === actionId);

  const loadDefects = useCallback(() => {
    if (!appId) {
      return;
    }
    getAppDefects({ appId }, (res) => setDefects(res?.defects || []));
  }, [appId]);

  // A different app is a different defect list, so drop back to the skeleton
  // until the new app's defects land.
  useEffect(() => {
    setDefects(null);
  }, [appId]);

  useEffect(() => {
    if (open) {
      loadDefects();
    }
  }, [open, loadDefects]);

  // Live updates: reload when a defect changes for the app this drawer shows.
  useEffect(() => {
    if (open && appId && appFixerUpdate?.ts && appFixerUpdate.appId === appId) {
      loadDefects();
    }
  }, [appFixerUpdate, open, appId, loadDefects]);

  const handleSubmit = () => {
    if (!category || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    reportAppDefect(
      {
        appId,
        actionId: actionId === NO_ACTION_VALUE ? undefined : actionId,
        category,
        reportText: (textareaRef.current?.value || '').trim() || undefined
      },
      (res) => {
        setIsSubmitting(false);
        if (res) {
          sendToast({ message: 'Defect reported successfully!', type: 'success', duration: 4000 });
          setCategory(null);
          setActionId(NO_ACTION_VALUE);
          if (textareaRef.current) {
            textareaRef.current.value = '';
          }
          hasTextRef.current = false;
          setHasText(false);
          loadDefects();
        } else {
          sendToast({ message: 'Failed to report defect.', type: 'error', duration: 4000 });
        }
      }
    );
  };

  return (
    <div className={`comp-argument-option-slider app-output-drawer ${open ? 'open' : ''}`}>
      <div className={'backing'} onClick={closeAppIssueReportDrawer} />
      <div className={'content'}>
        <SliderHeader title="Report Defect" onClose={closeAppIssueReportDrawer} />
        <Panel.Section title="New Defect" collapsable={false}>
          <Panel.Select //
            label="Category"
            required
            placeholder="Choose a category..."
            options={categoryOptions}
            value={selectedCategoryOption}
            onChange={(option) => setCategory(option?.value)}
          />
          <Panel.Select //
            label="Action"
            placeholder="No specific action"
            options={actionOptions}
            value={selectedActionOption}
            onChange={(option) => setActionId(option?.value ?? NO_ACTION_VALUE)}
          />
          <Input //
            label="What's wrong?"
            type="textarea"
            rows={5}
            maxLength={MAX_REPORT_LENGTH}
            ref={textareaRef}
            defaultValue=""
            placeholder="Describe what is broken and how to reproduce it..."
            onChange={handleReportInput}
          />
          <Button className="primary" onClick={handleSubmit} disabled={!category || !hasText || isSubmitting} loading={isSubmitting}>
            Submit
          </Button>
        </Panel.Section>
        <DividerV2 style={{ margin: '0px 10px' }} />
        <Panel.Section title="Reported Defects" collapsable={true}>
          {defects === null ? (
            <div className={'slider-content'}>
              <ScrollContainerV2 className="issue-list">
                <ReportedDefectsSkeleton />
              </ScrollContainerV2>
            </div>
          ) : defects.length > 0 ? (
            <div className={'slider-content'}>
              <ScrollContainerV2 className="issue-list">
                {defects.map((defect) => (
                  <AppEditorDefectListRow key={defect?.id} defect={defect} onChanged={loadDefects} />
                ))}
              </ScrollContainerV2>
            </div>
          ) : (
            <Panel.EmptyState title="No defects reported" description="Reported defects for this app will appear here." />
          )}
        </Panel.Section>
      </div>
    </div>
  );
};

export default AppEditorIssueReportDrawer;
